import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { HttpClient } from '@angular/common/http';
import { range } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { shareReplay, switchMap, catchError } from 'rxjs/operators';

function hexToBytes(hex: string): Uint8Array {
  hex = hex.replace(/[^0-9a-f]/ig, '');
  if (hex.length % 2 !== 0) {
    throw new Error('Hex string must have an even length!');
  }

  return new Uint8Array(
    range(0, hex.length, 2)
      .map(start => parseInt(hex.substr(start, 2), 16))
  );
}

function base64toBytes(s: string): Uint8Array {
  return Uint8Array.from(atob(s), c => c.charCodeAt(0));
}

function isEncrypted(data: any): data is EncryptedInput {
  return typeof data === 'object' && data && data.iv && data.payload && Object.keys(data).length === 2;
}

export interface EncryptedInput {
  iv: string;
  payload: string;
}

export interface EncryptedData {
  map?: {
    layerDefinitions: { [id: string]: object },
    layerDefinitionDefaults: { [id: string]: object },
  };
}

@Injectable()
export class EncryptedDataService {
  public data$: Observable<EncryptedData>;

  constructor(
    private authService: AuthService,
    http: HttpClient
  ) {
    this.data$ = authService.login$.pipe(
      switchMap(_ => http.get(`/assets/encrypted-data/T${this.authService.getTenant()}.json`)),
      // treat a missing .json as equivalent to an empty EncryptedData object
      catchError(_ => of({})),
      switchMap(response => EncryptedDataService.decrypt(response, this.authService.getTenantKey())),
      shareReplay(1),
    );
  }

  public static async decrypt(data: EncryptedInput | object, key: string): Promise<object> {
    // If called on data that isn't encrypted, simply return it;
    // this is especially useful with `npm run start`,
    // which doesn't serve the encrypted versions of files.
    if (!isEncrypted(data)) {
      return data;
    }

    const decrypted = await crypto.subtle.decrypt(
      {
        name: 'AES-CBC',
        iv: base64toBytes(data.iv),
      },
      await crypto.subtle.importKey('raw', hexToBytes(key), 'AES-CBC', false, ['decrypt']),
      base64toBytes(data.payload),
    );

    return JSON.parse(new TextDecoder().decode(decrypted));
  }
}
